import React, { useEffect, useState } from 'react';
import {
  FormDialog,
  InputField,
  MuiBox,
  SelectDropdown,
  Typography,
  Button,
  Tooltip,
  Icon
} from '@kis-technologies/kis-ui';
import { FeeInput } from 'components/FeeInput';
import { NULL_TICKET_TEMPLATE, NullTicketTemplate } from 'constants/forms';
import PriceTypesTable from './PriceTypesTable';
import PriceTypeOverlay from './PriceTypeOverlay';
import SouvenirEventIdsInput from './SouvenirEventIdsInput';
import { TicketTemplate } from 'store/ticketTemplates/types';
import { KioskTemplateOverlay } from 'views/KiosksView/components/KioskFormOverlay/KioskTemplateOverlay';
import { useStyles } from '../styled';
import { PriceType, Product, ProductOverlayProps } from '../types';
import { toPercentage } from 'utils/number';
import { getUniqueName } from 'utils/stringFormat';

const ProductOverlay: React.FC<ProductOverlayProps> = (props: ProductOverlayProps) => {
  const {
    mode,
    initialData,
    onClose,
    onSave,
    templates = [],
    onTemplatesUpdated,
    permissions,
    defaultKisProductFee,
    customFieldLabels
  } = props;

  const customFieldSlots = (['custom1', 'custom2', 'custom3', 'custom4', 'custom5'] as const).filter(
    (slot) => customFieldLabels?.[slot]
  );

  const classes = useStyles();

  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);
  const [formData, setFormData] = useState<Product>({
    id: '',
    name: '',
    ticketTemplateId: null,
    kisFeeAmount: null,
    kisFeePercent: null,
    enabled: false,
    priceTypes: [],
    configuration: { souvenirEventIds: [], customFields: {} }
  });
  const [priceTypeOverlayOpen, setPriceTypeOverlayOpen] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState<PriceType | null>(null);
  const [deletePriceTypeOverlayOpen, setDeletePriceTypeOverlayOpen] = useState(false);
  const [priceTypeToDelete, setPriceTypeToDelete] = useState<PriceType | null>(null);

  const templateOptions = [NULL_TICKET_TEMPLATE, ...templates];

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        ...initialData
      });
    } else if (mode === 'create' && defaultKisProductFee) {
      setFormData((prev) => ({
        ...prev,
        kisFeeAmount: defaultKisProductFee.amount,
        kisFeePercent: toPercentage(defaultKisProductFee.percentage)
      }));
    }
  }, [mode, initialData, defaultKisProductFee]);

  const handleSave = () => {
    onSave({
      ...formData
    });
  };

  const handleCopyPriceType = (priceType: PriceType) => {
    const copiedPriceType: PriceType = {
      ...priceType,
      id: Date.now().toString(),
      name: getUniqueName(priceType.name, formData.priceTypes)
    };

    setFormData((prev) => ({
      ...prev,
      priceTypes: [...prev.priceTypes, copiedPriceType]
    }));

    setSelectedPriceType(copiedPriceType);
    setPriceTypeOverlayOpen(true);
  };

  const handleDeletePriceType = () => {
    if (!priceTypeToDelete) return;

    setFormData((prev) => ({
      ...prev,
      priceTypes: prev.priceTypes.filter((p) => p.id !== priceTypeToDelete.id)
    }));

    setDeletePriceTypeOverlayOpen(false);
    setPriceTypeToDelete(null);
  };

  return (
    <FormDialog
      open
      title={mode === 'edit' ? 'Edit Product' : 'Create Product'}
      confirmButtonAction={handleSave}
      confirmButtonText="Save"
      confirmButtonDisabled={!permissions.canSaveProduct || !formData.name.trim() || formData.priceTypes.length === 0}
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
    >
      <MuiBox mt="10px">
        {/* Name */}
        <MuiBox className={classes.formBox}>
          <InputField
            label="Name"
            size="small"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            sx={{ width: '262px', height: '45px' }}
            disabled={!permissions.canEditProduct}
          />
        </MuiBox>

        {/* Template */}
        <MuiBox className={classes.formBox}>
          <Typography
            className={classes.formLabel}
            sx={{ display: 'flex', justifyContent: 'space-between', width: '262px' }}
          >
            Template{' '}
            {permissions.canEditProduct && (
              <Tooltip title={'Manage Templates'} placement="top">
                <Icon
                  name="Settings"
                  sx={{ cursor: 'pointer', mr: '10px' }}
                  onClick={() => setTemplateOverlayOpen(true)}
                />
              </Tooltip>
            )}
          </Typography>
          <SelectDropdown
            items={templateOptions}
            itemToString={(item: TicketTemplate | NullTicketTemplate) =>
              `${item.name}${item.type ? ` (${item.type})` : ''}`
            }
            selectedItem={templateOptions.find((t) => t.id === formData.ticketTemplateId) || NULL_TICKET_TEMPLATE}
            onChange={(item: TicketTemplate | NullTicketTemplate) =>
              setFormData((prev) => ({
                ...prev,
                ticketTemplateId: item?.id ?? null
              }))
            }
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: '#000'
              }
            }}
            disabled={!permissions.canEditProduct}
          />
        </MuiBox>

        {/* Souvenir Events */}
        <MuiBox className={classes.formBox}>
          <Typography className={classes.formLabel}>Souvenir Events</Typography>
          <SouvenirEventIdsInput
            value={formData.configuration.souvenirEventIds ?? []}
            onChange={(ids) =>
              setFormData((prev) => ({
                ...prev,
                configuration: { ...prev.configuration, souvenirEventIds: ids }
              }))
            }
            disabled={!permissions.canEditProduct}
          />
        </MuiBox>

        {/* Custom Fields */}
        {customFieldSlots.length > 0 && (
          <MuiBox className={classes.formBox}>
            <Typography className={classes.formLabel}>Custom Fields</Typography>
            <MuiBox display="flex" flexWrap="wrap" gap="16px">
              {customFieldSlots.map((slot) => (
                <MuiBox key={slot} sx={{ width: '262px' }}>
                  <InputField
                    label={customFieldLabels?.[slot]}
                    size="small"
                    value={formData.configuration.customFields?.[slot] ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          customFields: { ...prev.configuration.customFields, [slot]: e.target.value }
                        }
                      }))
                    }
                    sx={{ width: '262px', height: '45px' }}
                    disabled={!permissions.canEditProduct}
                  />
                </MuiBox>
              ))}
            </MuiBox>
          </MuiBox>
        )}

        {/* Price Types Section */}
        <MuiBox pb="32px">
          <MuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight="bold">Price Types</Typography>

            {permissions.canCreatePriceType && (
              <Button
                onClick={() => {
                  setSelectedPriceType(null);
                  setPriceTypeOverlayOpen(true);
                }}
              >
                Create Price Type
              </Button>
            )}
          </MuiBox>

          <PriceTypesTable
            priceTypes={formData.priceTypes}
            templates={templates}
            onEdit={(pt: PriceType) => {
              setSelectedPriceType(pt);
              setPriceTypeOverlayOpen(true);
            }}
            onCopy={handleCopyPriceType}
            onDelete={(pt: PriceType) => {
              setPriceTypeToDelete(pt);
              setDeletePriceTypeOverlayOpen(true);
            }}
            onToggle={(pt: PriceType) => {
              setFormData((prev) => ({
                ...prev,
                priceTypes: prev.priceTypes.map((p) => (p.id === pt.id ? { ...p, enabled: !p.enabled } : p))
              }));
            }}
            permissions={{
              edit: permissions.canEditPriceType,
              copy: permissions.canCreatePriceType,
              delete: permissions.canDeletePriceType,
              toggle: permissions.canTogglePriceType
            }}
          />
        </MuiBox>

        {/* KIS Fee */}
        <FeeInput
          label="KIS Fee"
          amount={formData.kisFeeAmount ?? null}
          percentage={formData.kisFeePercent ?? null}
          disabled={!permissions.canEditKisFee}
          onAmountChange={(value: number | null) => setFormData((prev) => ({ ...prev, kisFeeAmount: value }))}
          onPercentageChange={(value: number | null) => setFormData((prev) => ({ ...prev, kisFeePercent: value }))}
        />
      </MuiBox>

      {priceTypeOverlayOpen && (
        <PriceTypeOverlay
          mode={selectedPriceType ? 'edit' : 'create'}
          templates={templates}
          initialData={selectedPriceType}
          onClose={() => setPriceTypeOverlayOpen(false)}
          onSave={(priceType: PriceType) => {
            setFormData((prev) => ({
              ...prev,
              priceTypes: selectedPriceType
                ? prev.priceTypes.map((p) => (p.id === priceType.id ? priceType : p))
                : [...prev.priceTypes, { ...priceType }]
            }));

            setPriceTypeOverlayOpen(false);
          }}
          onTemplatesUpdated={onTemplatesUpdated}
          permissions={permissions}
        />
      )}

      {templateOverlayOpen && (
        <KioskTemplateOverlay
          canEdit={permissions.canManageTemplates}
          onClose={() => {
            setTemplateOverlayOpen(false);
            onTemplatesUpdated();
          }}
        />
      )}

      {deletePriceTypeOverlayOpen && (
        <FormDialog
          open={deletePriceTypeOverlayOpen}
          confirmButtonAction={handleDeletePriceType}
          confirmButtonText="Delete"
          cancelButtonAction={() => {
            setDeletePriceTypeOverlayOpen(false);
            setPriceTypeToDelete(null);
          }}
          cancelButtonText="Cancel"
        >
          <MuiBox>
            <Typography fontSize={24}>Are you sure you want to delete price type?</Typography>
          </MuiBox>
        </FormDialog>
      )}
    </FormDialog>
  );
};

export default ProductOverlay;
